import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const ADMIN_PAGE_TITLES = {
  '/': 'Control Tower',
  '/control-tower': 'Control Tower',
  '/applications': 'Admin Dashboard',
  '/applications/vendor-analytics': 'Vendor Analytics',
  '/applications/user-management': 'User Management',
  '/applications/venue-verification': 'Venue Verification',
  '/applications/subscription-control': 'Subscription Control',
  '/applications/audit-logs': 'Audit Logs',
  '/applications/contact-messages': 'Contact Messages',
  '/applications/mobile-content': 'Mobile Content',
  '/applications/support-tickets': 'Support Tickets',
  '/applications/content-moderation': 'Content Moderation',
  '/applications/all-venues': 'All Venues',
  '/applications/venues': 'Venues Management',
  '/applications/overview': 'Overview',
  '/applications/categories': 'Category Management',
  '/applications/positions': 'Positions Management',
  '/applications/employees': 'Staff & Employees',
  '/applications/my-plan': 'My Plan & Quotas',
  '/user-management': 'User Management',
  '/venue-verification': 'Venue Verification',
  '/subscription-control': 'Subscription Control',
  '/employee-management': 'Staff & Employees',
  '/position-management': 'Positions Management',
  '/categories': 'Category Management',
  '/content-moderation': 'Content Moderation',
  '/mobile-content': 'Mobile Content',
  '/support-tickets': 'Support Tickets',
  '/audit-logs': 'Audit Logs',
  '/contact-messages': 'Contact Messages',
  '/notification': 'Notifications',
  '/calendar': 'Calendar',
  '/calendar-main': 'Calendar',
  '/maps': 'Venue Maps',
  '/pricing': 'Subscription Plans',
  '/reservations': 'Reservations',
  '/view-profile': 'Admin Profile',
  '/theme': 'Theme Settings',
  '/tos': 'Terms of Service',
  '/sign-in': 'Sign In',
  '/forgot-password': 'Forgot Password',
  '/reset-password': 'Reset Password',
  '/access-denied': 'Access Denied',
  '/error': 'Page Not Found',
};

const BUSINESS_PAGE_TITLES = {
  '/': "Discover the UAE's Best Venues",
  '/overview': 'Business Overview',
  '/applications': 'Business Dashboard',
  '/applications/overview': 'Business Overview',
  '/applications/my-plan': 'My Plan & Quotas',
  '/applications/all-venues': 'My Venues',
  '/my-venues': 'My Venues',
  '/add-venue': 'Add Venue',
  '/reservations': 'Reservations Management',
  '/all-venues': 'My Venues',
  '/pricing': 'Plans & Pricing',
  '/view-profile': 'Business Profile',
  '/sign-in': 'Sign In',
  '/sign-up': 'Sign Up',
  '/forgot-password': 'Forgot Password',
  '/terms-and-conditions': 'Terms & Conditions',
  '/privacy-policy': 'Privacy Policy',
  '/tos': 'Terms of Service',
  '/contact': 'Contact Us',
  '/access-denied': 'Access Denied',
  '/error': 'Page Not Found',
};

const RouteScrollToTop = () => {
  const { pathname, hash } = useLocation();

  const updateTitleAndMeta = useCallback(() => {
    // Determine if user is accessing the admin portal
    const isAdmin = 
      process.env.REACT_APP_APP_TYPE === 'admin' ||
      window.location.hostname.startsWith('admin.') ||
      window.location.hostname.includes('admin');

    // 1. Manage Robots Meta for SEO & Security
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      document.head.appendChild(robotsMeta);
    }

    if (isAdmin) {
      // Admin consoles MUST NEVER be indexed by search engines
      robotsMeta.content = 'noindex, nofollow, noarchive, nosnippet';
    } else {
      // Public / Business marketing pages CAN be indexed
      robotsMeta.content = 'index, follow';
    }

    // 2. Manage Dynamic Page Title
    let pageTitle = '';
    const titleMap = isAdmin ? ADMIN_PAGE_TITLES : BUSINESS_PAGE_TITLES;

    if (titleMap[pathname]) {
      pageTitle = titleMap[pathname];
    } else if (pathname.startsWith('/edit-venue/')) {
      pageTitle = 'Edit Venue';
    } else if (pathname.startsWith('/venue-detail/') || pathname.startsWith('/venue/')) {
      pageTitle = 'Venue Details';
    } else if (pathname.startsWith('/view-profile/')) {
      pageTitle = isAdmin ? 'Admin Profile' : 'User Profile';
    } else if (pathname.startsWith('/reset-password/')) {
      pageTitle = 'Reset Password';
    } else if (pathname.startsWith('/applications/')) {
      const sub = pathname.replace('/applications/', '').replace(/-/g, ' ');
      pageTitle = sub
        .split(' ')
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    }

    const brandName = window.__SITE_TITLE__ || 'HubScroll';
    const brandSuffix = isAdmin ? `${brandName} Admin` : brandName;
    if (pageTitle) {
      document.title = `${pageTitle} | ${brandSuffix}`;
    } else {
      document.title = isAdmin ? `${brandName} Admin Portal` : `${brandName} - Discover & Manage UAE Venues`;
    }
  }, [pathname]);

  useEffect(() => {
    updateTitleAndMeta();

    // Listen for settings load or site title updates
    window.addEventListener('titleUpdateNeeded', updateTitleAndMeta);
    window.addEventListener('settingsChanged', updateTitleAndMeta);

    // Scroll to top or anchor target
    if (hash) {
      const id = hash.replace('#', '');
      const timer = setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.scrollTo(0, 0);
        }
      }, 150);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('titleUpdateNeeded', updateTitleAndMeta);
        window.removeEventListener('settingsChanged', updateTitleAndMeta);
      };
    } else {
      window.scrollTo(0, 0);
    }

    return () => {
      window.removeEventListener('titleUpdateNeeded', updateTitleAndMeta);
      window.removeEventListener('settingsChanged', updateTitleAndMeta);
    };
  }, [pathname, hash, updateTitleAndMeta]);

  return null;
};

export default RouteScrollToTop;
